import { useEffect, useState } from "react";
import ComponentCard from "../../common/ComponentCard";
import Label from "../../form/Label";
import InputField from '../../form/input/InputField';
import Select from '../../form/Select';
import MultiSelectCheckbox from '../../form/MultiSelectCheckbox';
import Switch from "../../form/switch/Switch";
import Button from "../../ui/button/Button";
import Alert from "../../ui/alert/Alert";
import Radio from "../../../components/form/input/Radio";
import { useNavigate } from "react-router";
import axios from "axios";
import { authService } from "../../../services/authService";

interface CademiFormData {
    active: boolean;
    name: string;
    apiKey: string;
    productId: string;
    offerList: string[];
    deliveryType: 'single' | 'recurrence';
    cademiDelivery?: string;
}

interface EditCademiFormProps {
    cademiId?: string;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
    companyId?: string;
}

export default function EditCademiForm({ cademiId, onSuccess, onError, companyId: companyIdProp }: EditCademiFormProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<CademiFormData>({
        name: "",
        apiKey: "",
        productId: "",
        offerList: [],
        deliveryType: 'single',
        active: true,
        cademiDelivery: "",
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");

    const [loading, setLoading] = useState(false);
    const [offers, setOffers] = useState<any[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [deliveries, setDeliveries] = useState<Array<{ value: string; label: string }>>([]);
    const [deliveriesData, setDeliveriesData] = useState<any[]>([]);
    const [loadingOffers, setLoadingOffers] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [selectAllOffers, setSelectAllOffers] = useState(false);
    const [selectedOffers, setSelectedOffers] = useState<string[]>([]);
    const [apiKeyValidated, setApiKeyValidated] = useState(false);
    const [webhookUrl, setWebhookUrl] = useState<string>("");
    const [configUrl, setConfigUrl] = useState<string>("");
    const [loadingIntegration, setLoadingIntegration] = useState<boolean>(!!cademiId);

    const companyId = companyIdProp || authService.getCompanyId?.();

    // Valida a chave API da Cademi
    const validateCademiApiKey = async (apiKey: string) => {
        setLoading(true);
        setApiError("");
        setApiSuccess("");
        setDeliveries([]);
        setDeliveriesData([]);
        setWebhookUrl("");
        setConfigUrl("");
        try {
            const resp = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/membership/validate`,
                {
                    provider: "cademi",
                    apiKey,
                    deliveryType: formData.deliveryType // Enviar single ou recurrence
                },
                { headers: { 'Authorization': `Bearer ${authService.getToken()}` } }
            );

            if (resp.data && resp.data.success) {
                setApiKeyValidated(true);
                setApiSuccess("Chave validada com sucesso!");
                setApiError("");

                // Captura o webhook_url da resposta
                if (resp.data.deliveries?.webhook_url) {
                    setWebhookUrl(resp.data.deliveries.webhook_url);
                }

                // Agora pega as entregas corretamente!
                const deliveryArray = resp.data.deliveries?.deliveries || [];

                // Armazena os dados completos das deliveries
                setDeliveriesData(deliveryArray);

                // Mapeia para o select
                setDeliveries(deliveryArray.map((d: any) => ({
                    value: d.id,
                    label: d.name
                })));
                await fetchOffers();
                await fetchProducts();
            } else {
                // Captura mensagem de erro específica do backend
                const errorMessage = resp.data?.message || resp.data?.error || "Chave inválida.";
                setApiError(errorMessage);
                setApiSuccess("");
                setApiKeyValidated(false);
                setDeliveries([]);
                setDeliveriesData([]);
                setWebhookUrl("");
                setConfigUrl("");
            }
        } catch (error: any) {
            // Captura erro de requisição HTTP e extrai mensagem específica
            let errorMessage = "Chave inválida ou erro na validação.";

            if (error.response?.data) {
                errorMessage = error.response.data.message || error.response.data.error || errorMessage;
            }

            setApiError(errorMessage);
            setApiSuccess("");
            setApiKeyValidated(false);
            setDeliveries([]);
            setDeliveriesData([]);
            setWebhookUrl("");
            setConfigUrl("");
        } finally {
            setLoading(false);
        }
    };

    // Carrega os dados da integração para edição
    useEffect(() => {
        const fetchIntegration = async () => {
            if (!cademiId) {
                setLoadingIntegration(false);
                return;
            }
            try {
                setLoadingIntegration(true);
                setApiError("");
                const resp = await axios.get(
                    `${import.meta.env.VITE_API_URL}/api/membership/${cademiId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${authService.getToken()}`,
                        },
                    }
                );

                const raw = resp.data?.data ?? resp.data;
                if (!raw) {
                    setApiError("Não foi possível carregar a integração.");
                    return;
                }

                // Alguns campos podem vir dentro de settingsData dependendo do backend
                const settings = raw.settingsData || {};
                const loadedName = raw.name || settings.name || "";
                const loadedApiKey = raw.apiKey || settings.apiKey || "";
                const loadedProductId = raw.productId || settings.productId || "";
                const loadedOffers: string[] = raw.offersList || raw.offerList || settings.offersList || [];
                const loadedType: 'single' | 'recurrence' = (raw.type || settings.type || 'single');
                const loadedActive: boolean = raw.active !== false;
                const loadedDeliveryId = String(raw.externalDeliveryId || settings.externalDeliveryId || "");
                const loadedWebhookUrl = raw.webhookUrl || settings.webhookUrl || "";
                const loadedConfigUrl = raw.configUrl || settings.configUrl || "";

                // Preenche dados básicos
                setFormData(prev => ({
                    ...prev,
                    name: loadedName,
                    apiKey: loadedApiKey,
                    productId: loadedProductId,
                    offerList: loadedOffers,
                    deliveryType: loadedType,
                    active: loadedActive,
                    cademiDelivery: "",
                }));

                // Valida a chave para popular deliveries e URLs
                if (loadedApiKey) {
                    try {
                        await validateCademiApiKey(loadedApiKey);
                    } catch (e) {
                        // Em caso de erro de validação, usuário poderá tentar novamente
                    }
                }

                // Carrega produtos e ofertas para o produto selecionado
                if (loadedProductId) {
                    await handleProductChange(loadedProductId);
                } else {
                    await fetchProducts();
                }

                // Ajusta seleção de ofertas baseado no que foi carregado
                if (!loadedOffers || loadedOffers.length === 0) {
                    setSelectAllOffers(true);
                    setSelectedOffers(['all']);
                    setFormData(prev => ({ ...prev, offerList: [] }));
                } else {
                    setSelectAllOffers(false);
                    setSelectedOffers(loadedOffers);
                    setFormData(prev => ({ ...prev, offerList: loadedOffers }));
                }

                // Ajusta entrega selecionada e configUrl
                if (loadedDeliveryId) {
                    setFormData(prev => ({ ...prev, cademiDelivery: loadedDeliveryId }));
                    // Se já temos deliveriesData, tenta achar o config_url
                    const found = deliveriesData.find(d => String(d.id) === String(loadedDeliveryId));
                    if (found?.config_url) {
                        setConfigUrl(found.config_url);
                    } else if (loadedConfigUrl) {
                        setConfigUrl(loadedConfigUrl);
                    }
                }

                // Caso a validação tenha trazido o webhook_url, mantém. Senão aplica o salvo
                if (!webhookUrl && loadedWebhookUrl) {
                    setWebhookUrl(loadedWebhookUrl);
                }
            } catch (error: any) {
                setApiError(error.response?.data?.error || error.message || "Erro ao carregar integração");
            } finally {
                setLoadingIntegration(false);
            }
        };

        fetchIntegration();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cademiId]);

    // Busca as ofertas da Empresa (após chave validada)
    const fetchOffers = async () => {
        setLoadingOffers(true);
        setOffers([]);
        try {
            const resp = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/company-offers/${companyId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );
            if (resp.data && resp.data.success && Array.isArray(resp.data.data)) {
                const mappedOffers = resp.data.data.map((offer: any) => ({
                    value: offer.id,
                    label: offer.name
                }));
                setOffers(mappedOffers);
            }
        } catch (error) {
            setOffers([]);
            setApiError("Erro ao buscar ofertas.");
        } finally {
            setLoadingOffers(false);
        }
    };

    // Busca as ofertas de um produto específico
    const fetchProductOffers = async (productId: string) => {
        setLoadingOffers(true);
        setOffers([]);
        try {
            const resp = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products/${productId}/offers-with-links`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );
            if (resp.data && resp.data.offers && Array.isArray(resp.data.offers)) {
                const mappedOffers = resp.data.offers.map((offer: any) => ({
                    value: offer.id,
                    label: offer.name
                }));
                setOffers(mappedOffers);
            }
        } catch (error) {
            setOffers([]);
            setApiError("Erro ao buscar ofertas do produto.");
        } finally {
            setLoadingOffers(false);
        }
    };

    // Busca os produtos da empresa (após chave validada)
    const fetchProducts = async () => {
        setLoadingProducts(true);
        setProducts([]);
        try {
            const resp = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );
            if (resp.data && resp.data.products && Array.isArray(resp.data.products)) {
                const mappedProducts = resp.data.products.map((product: any) => ({
                    value: product.id,
                    label: product.name
                }));
                setProducts(mappedProducts);
            }
        } catch (error) {
            setProducts([]);
            setApiError("Erro ao buscar produtos.");
        } finally {
            setLoadingProducts(false);
        }
    };

    // Ao alterar a chave, reseta estados dependentes. Alterar 'name' não deve resetar o formulário
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value } = e.target;
        setErrors((prev) => ({ ...prev, [id]: "" }));
        setFormData((prev) => ({ ...prev, [id]: value }));
        if (id === 'apiKey') {
            setApiError("");
            setApiSuccess("");
            setApiKeyValidated(false);
            setDeliveries([]);
            setDeliveriesData([]);
            setProducts([]);
            setOffers([]);
            setWebhookUrl("");
            setConfigUrl("");
        }
    };

    const handleSwitchChange = (id: string, checked: boolean) => {
        setFormData((prev) => ({ ...prev, [id]: checked }));
    };

    const handleOffersChange = (values: string[]) => {
        const isAllSelected = values.includes('all');
        setErrors((prev) => ({ ...prev, offerList: "" }));
        setApiError("");
        setApiSuccess("");
        if (isAllSelected) {
            setSelectAllOffers(true);
            setSelectedOffers(['all']);
            setFormData((prev) => ({
                ...prev,
                offerList: [], // Array vazio = qualquer oferta
            }));
        } else {
            setSelectAllOffers(false);
            setSelectedOffers(values);
            setFormData((prev) => ({
                ...prev,
                offerList: values, // Ofertas específicas selecionadas
            }));
        }
    };

    const handleDeliveryChange = (value: string) => {
        const selectedDelivery = deliveriesData.find(delivery => delivery.id === value);
        if (selectedDelivery) {
            setConfigUrl(selectedDelivery.config_url || "");
            setFormData((prev) => ({
                ...prev,
                cademiDelivery: value,
            }));
        } else {
            setConfigUrl("");
            setFormData((prev) => ({
                ...prev,
                cademiDelivery: value,
            }));
        }
        setErrors((prev) => ({ ...prev, cademiDelivery: "" }));
    };

    const handleProductChange = async (value: string) => {
        setFormData((prev) => ({ ...prev, productId: value }));
        await fetchProductOffers(value);
    };

    const handleDeliveryTypeChange = (value: 'single' | 'recurrence') => {
        setFormData((prev) => ({ ...prev, deliveryType: value }));
        // Reset dados de entrega quando mudar o tipo
        setDeliveries([]);
        setDeliveriesData([]);
        setApiKeyValidated(false);
        setWebhookUrl("");
        setConfigUrl("");
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.name) newErrors.name = "Nome da integração é obrigatório";
        if (!formData.apiKey) newErrors.apiKey = "Chave API é obrigatória";
        if (apiKeyValidated && !formData.productId) newErrors.productId = "Selecione um produto";
        if (apiKeyValidated && !selectAllOffers && !formData.offerList.length)
            newErrors.offerList = "Selecione ao menos uma oferta ou 'Qualquer oferta'";
        if (!formData.cademiDelivery) newErrors.cademiDelivery = "Selecione uma entrega";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);
        setApiError("");
        setApiSuccess("");
        try {
            const integrationData: any = {
                name: formData.name,
                provider: "cademi",
                apiKey: formData.apiKey,
                apiUrl: 'https://api.cademi.cloud/v2',
                companyId,
                // Sempre enviamos o productId selecionado
                productId: formData.productId,
                externalDeliveryId: formData.cademiDelivery ? String(formData.cademiDelivery) : null,
                offersList: formData.offerList,
                active: formData.active,
                type: formData.deliveryType, // single ou recurrence
                webhookUrl: webhookUrl,
                configUrl: configUrl,
            };
            const response = await axios.put(
                `${import.meta.env.VITE_API_URL}/api/membership/${cademiId}`,
                integrationData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`,
                    },
                }
            );
            // Considera sucesso se a API retornar status 200 e um objeto com ID
            if (response.status === 200 && (response.data && (response.data.id || response.data.success))) {
                setApiSuccess("Integração atualizada com sucesso!");
                if (onSuccess) onSuccess(response.data);
                setTimeout(() => navigate('/apps/cademi'), 1200);
            } else {
                setApiError(response.data?.error || "Erro ao atualizar integração");
                if (onError) onError(response.data?.error || "Erro desconhecido");
            }
        } catch (error: any) {
            setApiError(error.response?.data?.error || error.message || "Erro ao atualizar integração");
            if (onError) onError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleGoBack = () => navigate(`/apps/cademi`);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="col-span-1 lg:col-span-2">
                <ComponentCard title="Editar integração Cademí">
                    {/* Mensagens de erro e sucesso */}
                    {apiError && (
                        <div className="mt-4">
                            <Alert
                                variant="error"
                                title="Erro na validação"
                                message={apiError}
                            />
                        </div>
                    )}

                    {apiSuccess && (
                        <div className="mt-4">
                            <Alert
                                variant="success"
                                title="Sucesso"
                                message={apiSuccess}
                            />
                        </div>
                    )}

                    <form onSubmit={(e) => { e.preventDefault(); if (apiKeyValidated && !apiError) handleSubmit(); }}>
                        {loadingIntegration && (
                            <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                Carregando integração...
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Switch
                                label="Ativar integração"
                                checked={formData.active}
                                onChange={(checked) => handleSwitchChange("active", checked)}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="name">Nome da integração *</Label>
                            <InputField
                                type="text"
                                id="name"
                                placeholder="Informe um nome para identificação"
                                value={formData.name}
                                onChange={handleInputChange}
                                error={!!errors.name}
                                hint={errors.name}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="apiKey">Chave API *</Label>
                            <InputField
                                type="text"
                                id="apiKey"
                                placeholder="Sua chave API"
                                value={formData.apiKey}
                                onChange={handleInputChange}
                                error={!!errors.apiKey}
                                hint={errors.apiKey}
                                disabled={apiKeyValidated && !apiError} // Só desabilita se validado com sucesso (sem erro)
                            />
                        </div>

                        <div className="mt-6">
                            <Label>Tipo de entrega *</Label>
                            <div className="flex flex-row gap-6 mt-5">
                                <Radio
                                    id="deliveryType-single"
                                    name="deliveryType"
                                    value="single"
                                    checked={formData.deliveryType === 'single'}
                                    label="Única"
                                    onChange={() => handleDeliveryTypeChange('single')}
                                />
                                <Radio
                                    id="deliveryType-recurrence"
                                    name="deliveryType"
                                    value="recurrence"
                                    checked={formData.deliveryType === 'recurrence'}
                                    label="Recorrente"
                                    onChange={() => handleDeliveryTypeChange('recurrence')}
                                />
                            </div>
                        </div>

                        {/* Botão de validação sempre aparece quando não está validado OU quando há erro */}
                        {(!apiKeyValidated || apiError) && (
                            <div className="mt-6 flex justify-end gap-4.5">
                                <Button
                                    size="md"
                                    variant="primary"
                                    onClick={() => validateCademiApiKey(formData.apiKey)}
                                    disabled={loading || !formData.apiKey || !formData.name}
                                >
                                    {loading ? "Validando..." : "Validar chave API"}
                                </Button>
                            </div>
                        )}

                        {/* Segunda parte só se a chave foi validada E não tiver erro */}
                        {apiKeyValidated && !apiError && (
                            <>
                                <div className="mt-5">
                                    <Label>Produto *</Label>
                                    <Select
                                        options={products}
                                        placeholder="Selecione um produto"
                                        onChange={handleProductChange}
                                        value={formData.productId}
                                        className={errors.productId ? "border-red-500" : ""}
                                    />
                                    {errors.productId && <p className="mt-1 text-sm text-red-500">{errors.productId}</p>}
                                    {loadingProducts && (
                                        <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                            <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                            Carregando produtos...
                                        </div>
                                    )}
                                </div>

                                <div className="mt-5">
                                    <Label>Ofertas *</Label>
                                    {!formData.productId ? (
                                        <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                Selecione um produto primeiro
                                            </p>
                                        </div>
                                    ) : loadingOffers ? (
                                        <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                            <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                            Carregando ofertas...
                                        </div>
                                    ) : (
                                        <div className="mt-2">
                                            <MultiSelectCheckbox
                                                options={[
                                                    {
                                                        id: 'all',
                                                        label: 'Qualquer oferta',
                                                        disabled: false,
                                                    },
                                                    ...offers.map((offer) => ({
                                                        id: offer.value,
                                                        label: `${offer.label}`,
                                                        disabled: false,
                                                        meta: offer,
                                                    })),
                                                ]}
                                                selectedValues={selectAllOffers ? ['all'] : selectedOffers}
                                                onChange={handleOffersChange}
                                                placeholder="Selecione a oferta"
                                                error={!!errors.offerList}
                                                hint={errors.offerList}
                                                searchPlaceholder="Pesquisar ofertas"
                                                showSelectAll={false}
                                            />
                                            {selectAllOffers && (
                                                <p className="mt-1 text-xs text-blue-500">
                                                    Esta integração será válida para todas as ofertas, incluindo futuras.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-5">
                                    <Label>Entrega *</Label>
                                    <Select
                                        options={deliveries}
                                        placeholder="Selecione uma entrega"
                                        onChange={handleDeliveryChange}
                                        value={formData.cademiDelivery}
                                        className={errors.cademiDelivery ? "border-red-500" : ""}
                                    />
                                    {errors.cademiDelivery && <p className="mt-1 text-sm text-red-500">{errors.cademiDelivery}</p>}
                                </div>

                                <div className="mt-6 flex justify-end gap-4.5">
                                    <Button
                                        size="md"
                                        variant="outline"
                                        onClick={handleGoBack}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        size="md"
                                        variant="primary"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                    >
                                        {loading ? "Salvando..." : "Salvar alterações"}
                                    </Button>
                                </div>
                            </>
                        )}
                    </form>
                </ComponentCard>
            </div>
        </div>
    );
}
